import { useState, useEffect, useRef, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Paperclip, Link2, Trash2, Loader2, FileText, ShieldQuestion } from "lucide-react";

const STRENGTH_TONE = {
  user_stated: "text-donkey-grey border-donkey-line",
  user_supplied: "text-donkey-orange border-donkey-orange/40",
  externally_corroborated: "text-[#60a5fa] border-[#60a5fa]/40",
  independently_verified: "text-donkey-green border-donkey-green/40",
};

// Evidence Strength ladder. An upload or link alone is only "user-supplied
// supporting material" — never automatically "verified".
const LADDER = [
  ["User-stated", "A claim you've made with nothing attached yet."],
  ["User-supplied supporting material", "A document or link you've provided. Shows you have something — but is not independently checked."],
  ["Externally corroborated", "Matches an independent public source (assigned during analysis, not by uploading)."],
  ["Independently verified", "Origin and claim genuinely verified by the system. Rarely applies yet."],
];

export default function EvidencePanel({ interviewId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [linkVal, setLinkVal] = useState("");
  const [noteVal, setNoteVal] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/interviews/${interviewId}/evidence`);
      setItems(data.evidence || []);
    } catch (e) { /* owner-only; ignore */ }
    finally { setLoading(false); }
  }, [interviewId]);

  useEffect(() => { load(); }, [load]);

  const post = async (fd) => {
    setBusy(true);
    try {
      await api.post(`/interviews/${interviewId}/evidence`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLinkVal(""); setNoteVal("");
      if (fileRef.current) fileRef.current.value = "";
      await load();
      toast.success("Evidence added");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not add evidence.");
    } finally { setBusy(false); }
  };

  const addLink = () => { if (!linkVal.trim()) return; const fd = new FormData(); fd.append("url", linkVal.trim()); fd.append("label", "Link"); post(fd); };
  const addNote = () => { if (!noteVal.trim()) return; const fd = new FormData(); fd.append("note", noteVal.trim()); post(fd); };
  const addFile = (e) => { const f = e.target.files?.[0]; if (!f) return; const fd = new FormData(); fd.append("file", f); fd.append("label", f.name); post(fd); };
  const del = async (id) => { try { await api.delete(`/evidence/${id}`); setItems((x) => x.filter((i) => i.id !== id)); } catch { toast.error("Could not delete."); } };

  return (
    <div className="mt-10 card-donkey rounded-2xl p-6" data-testid="evidence-panel">
      <h3 className="font-heading font-semibold text-donkey-warm text-lg flex items-center gap-2">
        <ShieldQuestion className="w-5 h-5 text-donkey-orange" /> Evidence — optional, but it strengthens your verdict
      </h3>
      <p className="mt-2 text-sm text-donkey-grey leading-relaxed">
        Attach anything that backs up what you've told me — a portfolio link, a document, screenshots of customer
        feedback, anonymised results. Attaching something makes it <span className="text-donkey-orange">user-supplied supporting material</span>;
        it is never treated as independently verified just because it exists.
      </p>

      <div className="mt-5 grid sm:grid-cols-2 gap-3">
        <div className="flex items-center gap-2">
          <Link2 className="w-4 h-4 text-donkey-grey shrink-0" />
          <Input value={linkVal} onChange={(e) => setLinkVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addLink()}
            placeholder="https://link-to-your-work…" className="bg-donkey-bg border-donkey-line h-9 text-sm" data-testid="evidence-link-input" />
          <Button size="sm" onClick={addLink} disabled={busy} className="bg-donkey-orange text-donkey-black font-semibold shrink-0" data-testid="add-evidence-link-btn">Add</Button>
        </div>
        <div className="flex items-center gap-2">
          <Paperclip className="w-4 h-4 text-donkey-grey shrink-0" />
          <input ref={fileRef} type="file" onChange={addFile} accept=".pdf,.png,.jpg,.jpeg,.webp,.csv,.txt,.docx"
            className="text-xs text-donkey-grey file:mr-2 file:rounded file:border-0 file:bg-donkey-orange file:text-donkey-black file:px-3 file:py-1.5 file:font-semibold" data-testid="evidence-file-input" />
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <FileText className="w-4 h-4 text-donkey-grey shrink-0" />
        <Input value={noteVal} onChange={(e) => setNoteVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()}
          placeholder="Or state a fact (no attachment) — e.g. 'I served 12 paying clients last year'" className="bg-donkey-bg border-donkey-line h-9 text-sm" data-testid="evidence-note-input" />
        <Button size="sm" variant="ghost" onClick={addNote} disabled={busy} className="text-donkey-grey border border-donkey-line shrink-0" data-testid="add-evidence-note-btn">Add</Button>
      </div>

      {loading ? (
        <p className="mt-5 text-sm text-donkey-grey flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>
      ) : items.length > 0 ? (
        <ul className="mt-5 space-y-2" data-testid="evidence-list">
          {items.map((e) => (
            <li key={e.id} className="flex items-center justify-between gap-3 rounded-lg bg-donkey-bg border border-donkey-line px-3 py-2">
              <div className="min-w-0">
                <p className="text-sm text-donkey-warm truncate">
                  {e.kind === "link" ? <a href={e.url} target="_blank" rel="noopener noreferrer" className="text-donkey-orange hover:underline">{e.url}</a>
                    : e.kind === "file" ? e.original_filename : e.note}
                </p>
                <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wide mt-1 ${STRENGTH_TONE[e.strength] || STRENGTH_TONE.user_stated}`} data-testid="evidence-strength">
                  {e.strength_label}
                </span>
              </div>
              <button onClick={() => del(e.id)} className="text-donkey-grey hover:text-donkey-red shrink-0" aria-label="Delete evidence" data-testid={`delete-evidence-${e.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-5 text-xs text-donkey-grey italic">No evidence attached yet.</p>
      )}

      <details className="mt-5">
        <summary className="text-xs text-donkey-grey cursor-pointer hover:text-donkey-warm">How evidence strength is graded</summary>
        <ul className="mt-3 space-y-2">
          {LADDER.map(([t, d]) => (
            <li key={t} className="text-xs text-donkey-grey"><span className="text-donkey-warm/90 font-medium">{t}:</span> {d}</li>
          ))}
        </ul>
      </details>
    </div>
  );
}
